import TrainingClassMeta from 'pollworker/src/components/training/TrainingClassMeta';
import { TrainingClassStatus, getTrainingStatusMessage } from 'pollworker/src/state/training';
import type { TrainingLmsOverlay, UserTrainingClass } from 'pollworker/src/state/training';
import { useMemo } from 'react';
import { Badge, Button, Progress } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';
import { LmsEnrollmentStatus } from 'shared/src/utils/lmsStatus';
import { Link } from 'wouter';

export interface RemoteCourseRowProps {
  trainingClass: UserTrainingClass;
  lms: TrainingLmsOverlay;
  completed?: boolean;
  onStart?: () => void;
  starting?: boolean;
}

function getCourseActionLabel(enrollmentStatus?: LmsEnrollmentStatus, completed?: boolean) {
  if (completed || enrollmentStatus === LmsEnrollmentStatus.Completed) return 'Review';
  if (enrollmentStatus === LmsEnrollmentStatus.InProgress) return 'Continue';

  return 'Start';
}

function remoteCourseTitle(lms: TrainingLmsOverlay, className: string) {
  const learnHouseName = lms.CourseName?.trim();
  if (learnHouseName && learnHouseName !== lms.LhCourseUuid) return learnHouseName;
  return className || 'Online course';
}

export default function RemoteCourseRow({ trainingClass, lms, completed, onStart, starting }: RemoteCourseRowProps) {
  const labels: string[] = [];

  if (trainingClass.IsExtraTraining) labels.push('Additional training');
  if (trainingClass.AttendanceNotRecorded) labels.push('Attendance not recorded');
  if (trainingClass.IsVoided && completed) labels.push('No longer required');

  const title = remoteCourseTitle(lms, trainingClass.ClassName);
  const totalPages = lms.TotalActivityCount ?? 0;
  const completedPages = lms.CompletedActivityCount ?? 0;
  const percentComplete = totalPages > 0 ? Math.round((completedPages / totalPages) * 100) : 0;
  const statusMessage = completed
    ? trainingClass.Status === TrainingClassStatus.Completed
      ? getTrainingStatusMessage(trainingClass)
      : 'Course completed'
    : trainingClass.Status === TrainingClassStatus.NeedsAction || trainingClass.Status === TrainingClassStatus.Scheduled
      ? null
      : getTrainingStatusMessage(trainingClass);
  const actionLabel = getCourseActionLabel(lms.EnrollmentStatus, completed);
  const courseHref = useMemo(() => (lms.LhCourseUuid ? `/training/courses/${encodeURIComponent(lms.LhCourseUuid)}?sessionId=${encodeURIComponent(lms.SessionId)}` : null), [lms]);
  const courseAction = useMemo(() => {
    if (courseHref && onStart && lms.EnrollmentStatus !== LmsEnrollmentStatus.Completed && lms.EnrollmentStatus !== LmsEnrollmentStatus.InProgress) {
      return (
        <Button size="sm" className="w-full" onClick={onStart} disabled={starting}>
          {actionLabel}
        </Button>
      );
    }

    if (courseHref) {
      return (
        <Link href={courseHref}>
          <Button size="sm" variant={completed ? 'outline' : 'default'} className="w-full">
            {actionLabel}
          </Button>
        </Link>
      );
    }

    return null;
  }, [lms, onStart, courseHref, actionLabel, completed, starting]);

  return (
    <div className="flex flex-col gap-4 rounded-md bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className={cn('text-base font-semibold', completed ? 'text-green-700' : 'text-gray-900')}>{title}</p>
          <Badge status="info" className="px-2 py-0.5 font-medium">
            Online course
          </Badge>
          {labels.map((label) => (
            <Badge key={label} status="blank" className="px-2 py-0.5 font-medium">
              {label}
            </Badge>
          ))}
        </div>
        <TrainingClassMeta trainingClass={trainingClass} statusMessage={statusMessage} completed={completed} />
      </div>
      {!completed && totalPages > 0 ? (
        <div className="flex min-w-0 items-center gap-3">
          <p className="whitespace-nowrap text-sm text-gray-700">
            {completedPages} of {totalPages}
          </p>
          <Progress className="h-2 w-20 sm:w-24" value={percentComplete} aria-label={`${title} progress`} />
        </div>
      ) : null}
      {courseAction ? <div className="flex w-full flex-shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">{courseAction}</div> : null}
    </div>
  );
}
